import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import './ThemeToggle.css';

/**
 * ThemeToggle - Appearance controls
 *
 * Two icon buttons: colour theme (Kiln dark ⇄ chalk light) and layout
 * density (compact/comfortable/spacious). Both read ThemeContext, which
 * persists the choice and stamps <html>.
 */
const DENSITY_LABELS = { compact: 'Compact', comfortable: 'Comfortable', spacious: 'Spacious' };
const DENSITY_NEXT = { compact: 'comfortable', comfortable: 'spacious', spacious: 'compact' };

export function ThemeToggle({ showDensity = true }) {
  const { density, toggleDensity, theme, toggleTheme } = useTheme();

  const isSpacious = density === 'spacious';
  const nextLabel = DENSITY_LABELS[DENSITY_NEXT[density]] || 'Comfortable';
  const isLight = theme === 'light';

  return (
    <div className="theme-toggle">
      {/* Colour theme: dark (default) ⇄ light */}
      <button
        className="theme-toggle__btn theme-toggle__btn--color"
        onClick={toggleTheme}
        aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
        aria-pressed={isLight}
        type="button"
      >
        {isLight ? (
          /* Moon — shown in light mode: activating it goes dark */
          <svg className="theme-toggle__icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />
          </svg>
        ) : (
          /* Sun — shown in dark mode: activating it goes light */
          <svg className="theme-toggle__icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="4.2" />
            <line x1="12" y1="2" x2="12" y2="4.5" />
            <line x1="12" y1="19.5" x2="12" y2="22" />
            <line x1="2" y1="12" x2="4.5" y2="12" />
            <line x1="19.5" y1="12" x2="22" y2="12" />
            <line x1="4.9" y1="4.9" x2="6.7" y2="6.7" />
            <line x1="17.3" y1="17.3" x2="19.1" y2="19.1" />
            <line x1="4.9" y1="19.1" x2="6.7" y2="17.3" />
            <line x1="17.3" y1="6.7" x2="19.1" y2="4.9" />
          </svg>
        )}
        <span className="theme-toggle__tooltip">
          {isLight ? 'Light → Dark' : 'Dark → Light'}
        </span>
      </button>

      {/* Density cycle: Compact → Comfortable → Spacious */}
      {showDensity && (
      <button
        className="theme-toggle__btn theme-toggle__btn--density"
        onClick={toggleDensity}
        aria-label={`Layout density: ${DENSITY_LABELS[density] || 'Comfortable'}. Activate to switch to ${nextLabel}.`}
        type="button"
      >
        {isSpacious ? (
          /* Compress icon - shown in spacious mode (squeeze inward) */
          <svg className="theme-toggle__icon" viewBox="0 0 24 24" aria-hidden="true">
            <line x1="7" y1="4" x2="17" y2="4" />
            <line x1="7" y1="20" x2="17" y2="20" />
            <polyline points="9,13 12,10 15,13" />
            <polyline points="9,11 12,14 15,11" />
          </svg>
        ) : (
          /* Expand icon - shown in compact mode (spread outward) */
          <svg className="theme-toggle__icon" viewBox="0 0 24 24" aria-hidden="true">
            <line x1="7" y1="4" x2="17" y2="4" />
            <line x1="7" y1="20" x2="17" y2="20" />
            <polyline points="9,10 12,7 15,10" />
            <polyline points="9,14 12,17 15,14" />
          </svg>
        )}
        <span className="theme-toggle__tooltip">
          {DENSITY_LABELS[density] || 'Comfortable'} &rarr; {nextLabel}
        </span>
      </button>
      )}
    </div>
  );
}

export default ThemeToggle;
